import type { SimCore, RacerInput, SimEvent } from '@/sim/SimCore';
import type { SimRacer } from '@/sim/SimRacer';
import { applySnapshot } from '@/net/snapshot';
import { netdiag } from '@/net/netdiag';
import type { Snapshot } from '@/net/protocol';

/** Used for a racer we have no input for yet (coast). */
const NEUTRAL: RacerInput = Object.freeze({ throttle: 0, steer: 0, useItem: false });

/** Rocket-League-style remote input decay (M105): a rival's last broadcast
 *  control is trusted at FULL strength for two snapshot intervals, then fades
 *  to neutral. Predicting a stale steer at full lock through a link stall
 *  carved rivals ever further off their real path — every extra held tick grew
 *  the correction that later had to fold out as a visible snap. Undershooting
 *  and catching up reads better than overshooting and rubber-banding back
 *  (the Psyonix GDC rule). Never engages on a live 20 Hz stream (refresh every
 *  6 ticks ≪ the 12-tick hold). */
const REMOTE_HOLD_TICKS = 12;
const REMOTE_DECAY_TICKS = 18;

/**
 * ClientPredictor.ts — the CLIENT simulation (Rocket-League-style FULL-WORLD
 * prediction). It runs one local SimCore and predicts EVERY racer forward, not
 * just the local one:
 *   - the local player from real input (instant response),
 *   - every rival from its last broadcast input (held until the next snapshot).
 * Because the same deterministic physics runs on both ends from the same inputs,
 * the client's world stays in lock-step with the server's — so a collision you
 * drive into is one the server also resolves, instead of you sliding THROUGH a
 * rival and getting snapped back. When a rival changes what it's doing, the next
 * snapshot corrects it (smoothed in ClientSession).
 *
 * On reconcile we overwrite to authoritative truth and REPLAY the local inputs
 * (rivals held at their last broadcast input) to re-derive the present. HOW
 * MUCH is replayed depends on how the host applies inputs — two modes:
 *
 *  'ack'   (the in-tab loopback host): inputs are applied ON ARRIVAL, so the
 *          present is "truth + every input the host has not consumed yet".
 *          Replay everything after `ack`.
 *
 *  'label' (the real servers, M143): inputs are SCHEDULED — the host applies
 *          the input labelled T at tick T. The client's clock label says
 *          where the present is, so replay exactly the ticks from the
 *          snapshot's tick up to the client's tick, each with the input the
 *          client labelled for it (holding the last one across a gap).
 *          Replaying "everything unacked" here was wrong whenever inputs ran
 *          late in a BURST (a TCP stall, a Wi-Fi hop): the unacked pile grew
 *          with the stall, the world was replayed that many ticks too far —
 *          and snapped back when the burst landed. On a merely lossy
 *          WebSocket that produced 15–20 unit corrections out of nothing
 *          (measured in the chaos rig); the server never does work through a
 *          late pile one tick at a time, it applies the newest and moves on.
 */
export type ReplayMode = 'ack' | 'label';

/** Ticks one reconcile may re-simulate (2 s). A healthy clock leads by one
 *  round trip plus the input buffer — far below this. Only a snapshot that
 *  arrived very LATE (the first ones through after a radio blackout) needs a
 *  deep replay, and the driver rations those (see WsDriver DEEP_REPLAY). */
export const MAX_REPLAY_LABEL = 240;
/** History kept without any snapshot pruning it (a long stall). */
const MAX_HISTORY = 360;

export class ClientPredictor {
  clientTick = 0;

  private history: { tick: number; input: RacerInput }[] = [];
  /** Backlog size at the last reconcile — a sudden jump marks a post-stall
   *  BURST that may replay deep (see the two-tier cap in reconcile). */
  private lastBacklog = 0;
  /** Every non-local racer's last broadcast control (held between snapshots). */
  private readonly remoteInputs = new Map<number, RacerInput>();
  /** Predict ticks since the last reconcile refreshed `remoteInputs` — drives
   *  the remote input decay (M105). 0 during a reconcile's replay, so replays
   *  hold the snapshot's inputs at full strength as before. */
  private ticksSinceReconcile = 0;
  /** Reused per-step input map (local input + each rival's held input). */
  private stepMap: Map<number, RacerInput> | null = null;

  constructor(
    readonly sim: SimCore,
    private readonly localId: number,
    private readonly mode: ReplayMode = 'ack',
  ) {}

  private inputs(localInput: RacerInput): Map<number, RacerInput> {
    const m = this.stepMap ?? (this.stepMap = new Map());
    const age = this.ticksSinceReconcile;
    const f =
      age <= REMOTE_HOLD_TICKS
        ? 1
        : Math.max(0, 1 - (age - REMOTE_HOLD_TICKS) / REMOTE_DECAY_TICKS);
    for (const r of this.sim.racers) {
      if (r.id === this.localId) {
        m.set(r.id, localInput);
        continue;
      }
      const held = this.remoteInputs.get(r.id) ?? NEUTRAL;
      m.set(
        r.id,
        f >= 1
          ? held
          : { throttle: held.throttle * f, steer: held.steer * f, useItem: held.useItem },
      );
    }
    return m;
  }

  /** Loading round (online feel): the events the PREDICTED world emitted
   *  during forward predict steps — the local player's own fire / deny /
   *  collect / ball-hit / pad / countdown beats, which used to wait a full
   *  round trip for the server's copy (RTT + up to 83 ms after the press).
   *  Replays (reconcile) deliberately do NOT collect: they re-step history
   *  the player already heard. Drained by the driver's takeEvents. */
  private localEvents: SimEvent[] = [];

  /** Advance the whole predicted world one tick. */
  predict(input: RacerInput, dt: number): void {
    this.history.push({ tick: this.clientTick, input });
    if (this.history.length > MAX_HISTORY) this.history.shift();
    this.sim.step(this.inputs(input), dt);
    if (this.sim.events.length) this.localEvents.push(...this.sim.events);
    this.clientTick++;
    this.ticksSinceReconcile++;
  }

  /** Events emitted by predict steps since the last call (see localEvents). */
  takeLocalEvents(): SimEvent[] {
    if (!this.localEvents.length) return this.localEvents;
    const e = this.localEvents;
    this.localEvents = [];
    return e;
  }

  /** Authoritative correction: hold every rival's latest control, snap to truth,
   *  then replay the local inputs to re-derive the present (see ReplayMode). */
  reconcile(snap: Snapshot, dt: number): void {
    this.ticksSinceReconcile = 0; // fresh remote inputs — full trust (M105)
    this.remoteInputs.clear();
    for (const [k, v] of Object.entries(snap.inputs)) this.remoteInputs.set(Number(k), v);
    applySnapshot(this.sim, snap);
    if (this.mode === 'label') this.replayByLabel(snap, dt);
    else this.replayUnacked(snap, dt);
  }

  /** 'label' mode: simulate ticks [snap.tick, clientTick) by their labels. */
  private replayByLabel(snap: Snapshot, dt: number): void {
    // The snapshot is the world at the START of tick `snap.tick`: everything
    // labelled below it is settled, whatever the server did with it.
    let i = 0;
    while (i < this.history.length && this.history[i].tick < snap.tick) i++;
    if (i > 0) this.history.splice(0, i);
    const n = Math.min(this.clientTick - snap.tick, MAX_REPLAY_LABEL);
    netdiag.replayLast = Math.max(0, n);
    if (n > netdiag.replayMax) netdiag.replayMax = n;
    if (n <= 0) return; // our label is at/behind the server: the snapshot IS the present
    // Until our own inputs take over, the server keeps applying the control
    // it currently holds for us — so do we, across any label gap.
    const mine = snap.inputs[this.localId];
    let hold: RacerInput = mine
      ? { throttle: mine.throttle, steer: mine.steer, useItem: false }
      : { ...NEUTRAL };
    let h = 0;
    const end = snap.tick + n;
    for (let t = snap.tick; t < end; t++) {
      while (h < this.history.length && this.history[h].tick < t) h++;
      const e = h < this.history.length && this.history[h].tick === t ? this.history[h] : null;
      if (e) {
        hold = { throttle: e.input.throttle, steer: e.input.steer, useItem: false };
        this.sim.step(this.inputs(e.input), dt);
      } else {
        this.sim.step(this.inputs(hold), dt);
      }
    }
  }

  /** 'ack' mode (arrival semantics): replay every input the host has not
   *  consumed yet. */
  private replayUnacked(snap: Snapshot, dt: number): void {
    const acked = snap.ack[this.localId] ?? -1;
    this.history = this.history.filter((h) => h.tick > acked);
    // Replay cap: each replayed tick is a FULL-WORLD sim step, and the backlog
    // grows with latency. Past the cap we accept the server's truth for the
    // oldest ticks; the resulting correction is folded into the render offsets
    // like any other. M69, two tiers: the SUSTAINED cap bounds the steady-state
    // cost on a genuinely slow link, while a BURST — the backlog spiking after
    // a link stall — may replay a full second so no recent input is dropped.
    const MAX_REPLAY_SUSTAINED = 60;
    const MAX_REPLAY_BURST = 120;
    const backlog = this.history.length;
    const cap = backlog > this.lastBacklog * 2 ? MAX_REPLAY_BURST : MAX_REPLAY_SUSTAINED;
    this.lastBacklog = backlog;
    if (backlog > cap) this.history.splice(0, backlog - cap);
    netdiag.replayLast = this.history.length;
    if (this.history.length > netdiag.replayMax) netdiag.replayMax = this.history.length;
    for (const h of this.history) this.sim.step(this.inputs(h.input), dt);
  }

  /** Seat the clock when the race's first snapshot arrives: nothing has been
   *  sent yet, so any label is a fresh start. */
  seat(tick: number): void {
    this.clientTick = tick;
    this.history.length = 0;
    this.lastBacklog = 0;
  }

  /** Move the label FORWARD without simulating (M143 — after a freeze too
   *  long to re-simulate). Labels only ever grow, so the server never has to
   *  wonder whether a client rewound; the skipped labels simply never exist,
   *  and both ends hold the last control across the gap. */
  skipAhead(ticks: number): void {
    if (ticks > 0) this.clientTick += ticks;
  }

  local(): SimRacer {
    const r = this.sim.racers.find((x) => x.id === this.localId);
    if (!r) throw new Error(`ClientPredictor: no local racer ${this.localId}`);
    return r;
  }

  get racers(): SimRacer[] {
    return this.sim.racers;
  }
}
